// Studio's own documentation for HollowScript's standard library, for
// completion, hover and the Reference panel. Owned here, not by the language:
// the native `tooling` build tells the editor *that* `round` or `length`
// exists and what type it has (`Analysis.completions`, `Analysis.hover`), but
// carries no prose and no machine-readable member list for methods, so the
// one-line explanations and the receiver grouping live in this file and are
// merged over the native answers by `completion.ts` and `hover.ts`.
//
// Source of truth: `hollow/lang/sstk-hollowscript/docs/reference/stdlib.md`
// (generated, so keep this in sync by hand against that, not against the
// implementation). If it drifts, the cost is a stale completion suggestion or
// hover doc, never a wrong diagnostic or a wrong run - both stay driven by
// the real checker and VM regardless of what is listed here. The native
// prelude is larger than this list (`atan2`, `clamp`, `parseInt`, the `json`
// and `time` namespaces and more): those still complete and hover, with the
// native type and no Studio prose, until someone writes some here.
//
// The reserved words are not listed here any more: they come from the lexer's
// own table (`keywords()` on the tooling build, surfaced as
// `CapabilityCatalog.keywords`), so a word the language adds cannot be missed.

export interface StdlibMember {
  readonly receiver: string;
  readonly signature: string;
  readonly doc: string;
}

/** Every method reference/stdlib.md documents as "wired into the real
 * checker and evaluator" (the free `success`/`failure` pair is listed
 * separately below - they're called bare, not as a member access). Always
 * available, never `uses`-gated - `stdlib.md`'s own framing. */
export const STDLIB_METHODS: readonly StdlibMember[] = [
  // Strings
  { receiver: 'String', signature: 'length(): Int', doc: 'The number of characters.' },
  { receiver: 'String', signature: 'contains(part: String): Bool', doc: 'Whether `part` occurs anywhere in this string.' },
  { receiver: 'String', signature: 'startsWith(prefix: String): Bool', doc: 'Whether this string begins with `prefix`.' },
  { receiver: 'String', signature: 'endsWith(suffix: String): Bool', doc: 'Whether this string ends with `suffix`.' },
  { receiver: 'String', signature: 'toUpper(): String', doc: 'An upper-cased copy.' },
  { receiver: 'String', signature: 'toLower(): String', doc: 'A lower-cased copy.' },
  { receiver: 'String', signature: 'trim(): String', doc: 'A copy with leading/trailing whitespace removed.' },
  { receiver: 'String', signature: 'split(separator: String): [String]', doc: 'Splits on every occurrence of `separator`.' },
  { receiver: 'String', signature: 'replace(from: String, to: String): String', doc: 'Replaces every occurrence of `from` with `to`.' },
  {
    receiver: 'String',
    signature: 'substring(start: Int, upTo: Int): String',
    doc: '`start` inclusive, `upTo` exclusive, both clamped to the string.',
  },
  // Numbers (free functions, called bare)
  { receiver: 'Number', signature: 'round(x: Float): Int', doc: 'Rounds to the nearest integer.' },
  { receiver: 'Number', signature: 'floor(x: Float): Int', doc: 'Rounds down.' },
  { receiver: 'Number', signature: 'ceil(x: Float): Int', doc: 'Rounds up.' },
  { receiver: 'Number', signature: 'sqrt(x: Float): Float', doc: 'Square root.' },
  { receiver: 'Number', signature: 'pow(base: Float, exponent: Float): Float', doc: 'Exponentiation.' },
  { receiver: 'Number', signature: 'abs(x): same type as x', doc: 'Absolute value; preserves Int vs Float.' },
  { receiver: 'Number', signature: 'min(a, b): Int if both are Int, otherwise Float', doc: 'The smaller of two numbers.' },
  { receiver: 'Number', signature: 'max(a, b): Int if both are Int, otherwise Float', doc: 'The larger of two numbers.' },
  { receiver: 'Number', signature: 'remainder(a: Int, b: Int): Int', doc: 'Integer remainder.' },
  { receiver: 'Number', signature: 'range(from: Int, upTo: Int): [Int]', doc: 'Empty when `from >= upTo`.' },
  // Lists
  { receiver: 'List', signature: 'length(): Int', doc: 'The number of elements.' },
  { receiver: 'List', signature: 'isEmpty(): Bool', doc: 'Whether the list has no elements.' },
  { receiver: 'List', signature: 'contains(item: T): Bool', doc: 'Whether `item` occurs anywhere in the list.' },
  { receiver: 'List', signature: 'first(): T?', doc: 'The first element, or none.' },
  { receiver: 'List', signature: 'last(): T?', doc: 'The last element, or none.' },
  { receiver: 'List', signature: 'append(item: T): [T]', doc: 'Returns a new list with `item` added.' },
  { receiver: 'List', signature: 'concat(other: [T]): [T]', doc: 'Returns a new list, this list followed by `other`.' },
  { receiver: 'List', signature: 'map<U>(transform: fn(T): U): [U]', doc: 'Transforms every element.' },
  { receiver: 'List', signature: 'filter(keep: fn(T): Bool): [T]', doc: 'Keeps elements `keep` returns true for.' },
  { receiver: 'List', signature: 'each(action: fn(T))', doc: 'Calls `action` once per element.' },
  { receiver: 'List', signature: 'reduce<A>(initial: A, combine: fn(A, T): A): A', doc: 'Folds the list to a single value.' },
  { receiver: 'List', signature: 'sort(): [T]', doc: 'T must be Int, Float or String.' },
  { receiver: 'List', signature: 'sort(compare: fn(T, T): Int): [T]', doc: 'Any T; negative/zero/positive orders it before/equal/after.' },
  { receiver: 'List', signature: 'reverse(): [T]', doc: 'Returns a new list in reverse order.' },
  { receiver: 'List', signature: 'join(separator: String): String', doc: 'T must be String.' },
  // Records
  { receiver: 'Record', signature: 'keys(r): [String]', doc: "A record's field names." },
  { receiver: 'Record', signature: 'has(r, name: String): Bool', doc: 'Whether the record has a field named `name`.' },
];

/** `success`/`failure` are called bare (`success(value)`), not as a member
 * of anything - kept as their own list rather than folded into
 * `STDLIB_METHODS`, whose `receiver` field would otherwise be misleading
 * for them. */
export const STDLIB_FREE_FUNCTIONS: readonly StdlibMember[] = [
  { receiver: 'Result', signature: 'success<T>(value: T): Result<T>', doc: 'Builds a successful `Result`.' },
  { receiver: 'Result', signature: 'failure<T>(message: String): Result<T>', doc: 'Builds a failed `Result` carrying `message`.' },
];

/** The callable name a `StdlibMember`'s `signature` starts with (`"map<U>(...)"`
 * -> `"map"`, `"length(): Int"` -> `"length"`) - stops at the first `(` or
 * `<`, whichever comes first, so a generic method's type parameter list
 * never leaks into the name. */
export function stdlibMemberName(member: StdlibMember): string {
  const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(member.signature);
  return match ? match[0] : member.signature;
}

/** Every stdlib member whose name matches `name`, optionally only those of one
 * receiver group. A hover on `.length` is ambiguous between `String` and
 * `List` until the receiver's type is known (`stdlibReceiverForType`); the
 * caller passes it when it is, and shows every candidate when it is not. */
export function stdlibMembersNamed(name: string, receiver?: string): readonly StdlibMember[] {
  return STDLIB_METHODS.filter((member) => stdlibMemberName(member) === name && (receiver === undefined || member.receiver === receiver));
}

/** The stdlib group a checker-rendered type belongs to (`String` -> `"String"`,
 * `[Int]` -> `"List"`), or `undefined` for any other type, including an
 * optional, whose members this file does not document. Used to narrow a
 * member hover or completion to the receiver's real type now that the checker
 * can say what it is. */
export function stdlibReceiverForType(typeText: string): 'String' | 'List' | undefined {
  if (typeText === 'String') return 'String';
  if (typeText.startsWith('[') && typeText.endsWith(']')) return 'List';
  return undefined;
}

/** The groups whose members are called bare (`round(x)`, `keys(r)`,
 * `success(v)`), not as `receiver.member()`. `STDLIB_METHODS` keeps the
 * number and record entries beside the true methods for historical reasons;
 * this is how the editor tells them apart. */
export const STDLIB_FREE_RECEIVERS: readonly string[] = ['Number', 'Record', 'Result'];

/** Every documented bare-call function: the number and record entries of
 * `STDLIB_METHODS` plus `STDLIB_FREE_FUNCTIONS`. */
export const STDLIB_ALL_FREE_FUNCTIONS: readonly StdlibMember[] = [
  ...STDLIB_METHODS.filter((member) => STDLIB_FREE_RECEIVERS.includes(member.receiver)),
  ...STDLIB_FREE_FUNCTIONS,
];

/** The documented members that are called on a receiver (`String`, `List`). */
export const STDLIB_RECEIVER_METHODS: readonly StdlibMember[] = STDLIB_METHODS.filter((member) => !STDLIB_FREE_RECEIVERS.includes(member.receiver));
